<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OmniCheckbox from "@/components/omni/OmniCheckbox.vue";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import BolNumberField from "@/views/trips/components/BolNumberField.vue";
import type { Bol, RouteStopDto, TravelOrderOrderInfo } from "@/api/trips/dto/trip.dto";
import type { TripRouteStop } from "@/types/trip";

const bols = defineModel<(Bol | null)[]>({ default: () => [] });
const notAvailable = defineModel<boolean>("notAvailable", { default: false });

const props = withDefaults(
  defineProps<{
    reusedBols: Bol[];
    routeStop: TripRouteStop | RouteStopDto;
    routeStops: (TripRouteStop | RouteStopDto)[];
    errors?: (string | null)[];
    readonly?: boolean;
    order?: TravelOrderOrderInfo | null;
  }>(),
  {
    errors: () => [],
    readonly: false,
    order: null,
  },
);

const { t } = useI18n();

const hasBols = computed<boolean>(() => {
  return bols.value.some((bol) => bol && !bol.isDeleted);
});

function addBol(): void {
  bols.value.push(null);
}

function isSavedBol(bol: Bol): boolean {
  return props.reusedBols.some((reusedBol) => reusedBol.id === bol.id);
}

function setBolDeletion(index: number, isDeleted: boolean): void {
  bols.value = bols.value.map((item, i) => {
    if (i !== index || !item) {
      return item;
    }

    return {
      ...item,
      isDeleted,
    };
  });
}

function removeBol(index: number): void {
  const bol = bols.value[index];

  if (!bol || !isSavedBol(bol)) {
    bols.value = bols.value.filter((item, i) => i !== index);

    return;
  }

  setBolDeletion(index, true);
}

function restoreBol(index: number): void {
  setBolDeletion(index, false);
}
</script>

<template>
  <omni-tooltip :disabled="!notAvailable" full-width>
    <template #activator>
      <template v-for="(item, i) in bols" :key="i">
        <bol-number-field
          v-model="bols[i]"
          :disabled="notAvailable"
          :error-message="props.errors[i]"
          :order="props.order"
          :readonly="props.readonly"
          :required="!notAvailable && !item?.isDeleted"
          :reused-bols="props.reusedBols"
          :route-stop="props.routeStop"
          :route-stops="props.routeStops"
          @delete="removeBol(i)"
          @restore="restoreBol(i)"
        />
      </template>
    </template>

    {{ t("trip.uncheck-not-available-to-add-bol") }}
  </omni-tooltip>

  <div class="d-flex justify-space-between">
    <v-btn
      v-if="!props.readonly"
      :disabled="notAvailable"
      class="font-size-12 font-weight-600 text-uppercase"
      color="primary"
      data-qa="add-bol-button"
      prepend-icon="mdi-plus"
      variant="text"
      @click="addBol"
    >
      Add BOL
    </v-btn>

    <omni-checkbox
      v-model="notAvailable"
      :disabled="hasBols"
      :label="t('general.not-available')"
      class="mb-4"
      data-qa="bol-not-available"
    />
  </div>
</template>

<style lang="scss" scoped></style>
